import { useState, useEffect, useRef } from 'react'
import { supabase } from './supabase'

function stripBrand(name, brand) {
  if (!brand || !name) return name || ''
  const escaped = brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return name.replace(new RegExp('^' + escaped + '\\s*', 'i'), '').trim() || name
}

export default function QuickShop({ user, onBack, onGoToCart }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [added, setAdded] = useState([])
  const [adding, setAdding] = useState(null)
  const inputRef = useRef(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (query.length < 2) { setResults([]); return }
    const timer = setTimeout(() => searchKroger(query), 300)
    return () => clearTimeout(timer)
  }, [query])

  async function searchKroger(q) {
    setSearching(true)
    try {
      const res = await fetch(`/api/kroger?query=${encodeURIComponent(q)}`)
      const data = await res.json()
      setResults((data.data || []).slice(0, 8).map(p => ({
        name: stripBrand(p.description, p.brand),
        brand: p.brand || '',
        size: p.items?.[0]?.size || '',
        price: p.items?.[0]?.price?.regular || 0,
        kroger_sku: p.productId,
      })))
    } catch {
      setResults([])
    }
    setSearching(false)
  }

  async function addToCart(item) {
    setAdding(item.name)

    const { data: membership } = await supabase
      .from('household_members')
      .select('household_id')
      .eq('user_id', user.id)
      .maybeSingle()
    if (!membership) { setAdding(null); return }

    await supabase.from('cart_items').insert({
      household_id: membership.household_id,
      name: item.name,
      brand: item.brand || '',
      size: item.size || '',
      price: item.price || 0,
      emoji: '🛒',
      kroger_sku: item.kroger_sku || null,
      quantity: 1,
      checked: false,
    })

    setAdded(prev => [item, ...prev])
    setAdding(null)
    setQuery('')
    setResults([])
  }

  return (
    <div style={styles.container}>
      <div style={styles.navBar}>
        <button style={styles.backBtn} onClick={onBack}>← Back</button>
        <div style={styles.navTitle}>Quick shop</div>
        {added.length > 0 ? (
          <button style={styles.cartBtn} onClick={onGoToCart}>Cart ({added.length})</button>
        ) : (
          <div style={{ width: 70 }} />
        )}
      </div>

      <div style={styles.searchWrap}>
        <div style={styles.searchRow}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            ref={inputRef}
            style={styles.searchInput}
            placeholder="Search for an item…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {searching && <span style={styles.spinner}>…</span>}
          {query.length > 0 && (
            <button style={styles.clearBtn} onClick={() => { setQuery(''); setResults([]) }}>✕</button>
          )}
        </div>
      </div>

      <div style={styles.content}>
        {query.length >= 2 && (
          <div style={styles.results}>
            <div style={styles.manualRow} onClick={() => addToCart({ name: query, brand: '', size: '', price: 0, kroger_sku: null })}>
              <div style={styles.resultInfo}>
                <div style={styles.resultName}>Add "{query}" manually</div>
                <div style={styles.resultMeta}>No price · custom item</div>
              </div>
              <div style={styles.resultRight}>
                <button style={styles.addBtn} disabled={adding === query}>
                  {adding === query ? '…' : '+'}
                </button>
              </div>
            </div>
            {results.map((item, i) => (
              <div key={i} style={styles.resultRow}>
                <div style={styles.resultInfo}>
                  <div style={styles.resultName}>{item.name}</div>
                  <div style={styles.resultMeta}>{item.brand}{item.size ? ` · ${item.size}` : ''}</div>
                </div>
                <div style={styles.resultRight}>
                  <span style={styles.resultPrice}>${item.price?.toFixed(2)}</span>
                  <button
                    style={styles.addBtn}
                    onClick={() => addToCart(item)}
                    disabled={adding === item.name}
                  >
                    {adding === item.name ? '…' : '+'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {query.length < 2 && added.length === 0 && (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>🛒</div>
            <div style={styles.emptyTitle}>Search for anything</div>
            <div style={styles.emptySub}>Items go straight to your cart — no bundle needed.</div>
          </div>
        )}

        {added.length > 0 && (
          <div style={styles.addedSection}>
            <div style={styles.addedLabel}>Added this session</div>
            {added.map((item, i) => (
              <div key={i} style={styles.addedRow}>
                <span style={styles.addedEmoji}>✓</span>
                <div style={styles.addedInfo}>
                  <div style={styles.addedName}>{item.name}</div>
                  <div style={styles.addedMeta}>{item.brand}{item.size ? ` · ${item.size}` : ''}</div>
                </div>
                <span style={styles.addedPrice}>${item.price?.toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {added.length > 0 && (
        <div style={styles.goToCartBar} onClick={onGoToCart}>
          <span style={styles.goToCartText}>Go to cart</span>
          <span style={styles.goToCartCount}>{added.length} item{added.length !== 1 ? 's' : ''} added</span>
        </div>
      )}
    </div>
  )
}

const styles = {
  container: {
    maxWidth: '430px', margin: '0 auto', minHeight: '100vh',
    background: 'white', display: 'flex', flexDirection: 'column',
    fontFamily: "'DM Sans', sans-serif",
  },
  navBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 20px 8px', flexShrink: 0,
  },
  backBtn: {
    fontSize: '13px', color: '#1D9E75', background: 'none',
    border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  navTitle: { fontFamily: "'DM Serif Display', serif", fontSize: '18px', color: '#1a1a18' },
  cartBtn: {
    fontSize: '13px', color: '#1D9E75', background: 'none',
    border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: '500',
  },
  searchWrap: { padding: '0 20px 12px', flexShrink: 0 },
  searchRow: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: '#f9f9f7', border: '0.5px solid #1D9E75',
    borderRadius: '12px', padding: '10px 14px',
  },
  searchIcon: { fontSize: '14px', flexShrink: 0 },
  searchInput: {
    flex: 1, background: 'none', border: 'none', outline: 'none',
    fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#1a1a18',
  },
  spinner: { fontSize: '14px', color: '#aaa' },
  clearBtn: {
    background: 'none', border: 'none', fontSize: '12px', color: '#aaa',
    cursor: 'pointer', padding: '0 2px',
  },
  content: { flex: 1, overflowY: 'auto', padding: '0 20px 16px' },
  results: {
    border: '0.5px solid #eee', borderRadius: '14px', overflow: 'hidden', marginBottom: '16px',
  },
  manualRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '12px 14px', borderBottom: '0.5px solid #e8f5f0',
    background: '#f4fbf8', cursor: 'pointer',
  },
  resultRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '12px 14px', borderBottom: '0.5px solid #f5f5f5',
  },
  resultInfo: { flex: 1, minWidth: 0 },
  resultName: {
    fontSize: '13px', fontWeight: '500', color: '#1a1a18',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  resultMeta: { fontSize: '11px', color: '#888', marginTop: '2px' },
  resultRight: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
  resultPrice: { fontSize: '13px', color: '#888' },
  addBtn: {
    width: '28px', height: '28px', borderRadius: '50%',
    border: '0.5px solid #1D9E75', background: '#E1F5EE',
    color: '#1D9E75', fontSize: '18px', fontWeight: '300',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '60px 20px', textAlign: 'center',
  },
  emptyIcon: { fontSize: '40px', marginBottom: '12px' },
  emptyTitle: { fontSize: '15px', fontWeight: '500', color: '#1a1a18', marginBottom: '6px' },
  emptySub: { fontSize: '13px', color: '#aaa', lineHeight: '1.6', maxWidth: '220px' },
  addedSection: { marginTop: '4px' },
  addedLabel: {
    fontSize: '11px', fontWeight: '600', color: '#aaa',
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px',
  },
  addedRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '9px 0', borderBottom: '0.5px solid #f5f5f5',
  },
  addedEmoji: { fontSize: '14px', color: '#1D9E75', width: '20px', textAlign: 'center', flexShrink: 0 },
  addedInfo: { flex: 1, minWidth: 0 },
  addedName: { fontSize: '13px', fontWeight: '500', color: '#1a1a18' },
  addedMeta: { fontSize: '11px', color: '#888' },
  addedPrice: { fontSize: '12px', color: '#888', flexShrink: 0 },
  goToCartBar: {
    margin: '6px 20px 14px', padding: '14px 18px', background: '#1D9E75',
    borderRadius: '14px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', cursor: 'pointer', flexShrink: 0,
  },
  goToCartText: { fontSize: '14px', fontWeight: '500', color: 'white' },
  goToCartCount: { fontSize: '12px', color: 'rgba(255,255,255,0.8)' },
}
